import { FC, ReactElement, ReactNode, cloneElement, useMemo } from 'react'

import { UserRole } from 'constants/enums/UserRolesEnum'
import { PUBLISHED_GAMES } from 'constants/routes'

import { useAuth } from 'contexts/AuthProvider'

import HomePage from 'pages/HomePage/HomePage'
import PublishedGame from 'pages/PublishedGame'

export type Props = {
	allowAnyAuthenticatedUser?: boolean
	children: ReactNode
	guestUserAllowed?: boolean
}

const RequireAuth: FC<Props> = ({
	children,
	guestUserAllowed = true,
	allowAnyAuthenticatedUser = false,
}) => {
	const { isAuthenticated, isAuthenticating, user } = useAuth()
	const name = user?.firstName
	const roleId = user?.session?.idToken?.payload?.role_id
	const isGameParticipant = !roleId || roleId === UserRole.Participant

	const canAccess = useMemo(() => {
		// Unauthenticated users: only guestUserAllowed pages are accessible
		if (!isAuthenticated) return guestUserAllowed

		// Authenticated users:
		// Exclude game participants for allowAnyAuthenticatedUser pages
		if (allowAnyAuthenticatedUser) return !isGameParticipant
		// For users with > than participant access, all pages are accessible
		if (!isGameParticipant) return true
		// Otherwise, for game participant users, only guestUserAllowed pages are accessible
		return guestUserAllowed
	}, [
		isAuthenticated,
		isGameParticipant,
		guestUserAllowed,
		allowAnyAuthenticatedUser,
	])

	return (
		<>
			{isAuthenticating ? (
				<span data-testid="require-auth__authenticating-message">
					...isAuthenticating
				</span>
			) : canAccess ? (
				cloneElement(children as ReactElement, {
					name,
				})
			) : window.location.href.includes(PUBLISHED_GAMES + '/') ? (
				<PublishedGame />
			) : (
				<HomePage />
			)}
		</>
	)
}

export default RequireAuth
